import {
  ProcessAutomationInvocation,
  ProcessAutomationWorkflow,
} from '../model/process-automation';

const CARD_SCHEMA = 'http://adaptivecards.io/schemas/adaptive-card.json';

/**
 * Builds choices whose value is the readable title, so the selected value
 * reads well in the task summary
 * @param titles - choice titles
 * @example toChoices(['Standard', 'Superior'])
 * @returns Adaptive Card choices
 */
const toChoices = (titles: string[]) =>
  titles.map((title) => ({ title, value: title }));

const PACKAGE_CHOICES = toChoices([
  'Golden Circle & Northern Lights',
  'Ring Road Self-Drive',
  'Reykjavik City Break',
]);

const HOTEL_CHOICES = toChoices(['Standard', 'Superior', 'Boutique']);

/** Trip form filled in by the Book an Iceland Trip task */
const icelandTripFormCard = {
  type: 'AdaptiveCard',
  $schema: CARD_SCHEMA,
  version: '1.6',
  body: [
    {
      type: 'Input.ChoiceSet',
      id: 'tripPackage',
      label: 'Package *',
      value: 'Golden Circle & Northern Lights',
      choices: PACKAGE_CHOICES,
    },
    {
      type: 'ColumnSet',
      columns: [
        {
          type: 'Column',
          width: 'stretch',
          items: [
            {
              type: 'Input.Date',
              id: 'departureDate',
              label: 'Departure Date *',
            },
          ],
        },
        {
          type: 'Column',
          width: 'stretch',
          items: [
            {
              type: 'Input.Date',
              id: 'returnDate',
              label: 'Return Date *',
            },
          ],
        },
      ],
    },
    {
      type: 'ColumnSet',
      columns: [
        {
          type: 'Column',
          width: 'auto',
          items: [
            {
              type: 'Input.Number',
              id: 'travelers',
              label: 'Travelers *',
              placeholder: 'Enter Travelers',
              min: 1,
            },
          ],
        },
        {
          type: 'Column',
          width: 'stretch',
          items: [
            {
              type: 'Input.Text',
              id: 'departureCity',
              label: 'Departure City *',
              placeholder: 'Enter City',
            },
          ],
        },
      ],
    },
    {
      type: 'Input.ChoiceSet',
      id: 'hotelType',
      label: 'Hotel Type',
      value: 'Superior',
      choices: HOTEL_CHOICES,
    },
  ],
};

const icelandTripOutput = {
  summary:
    'Iceland package booked: ${tripPackage}, ${departureDate} to ${returnDate}, ${travelers} travelers from ${departureCity}. Ref ${referenceNumber}.',
  customerMessage:
    'Your Iceland trip is booked! ${tripPackage} package, ${departureDate} to ${returnDate} for ${travelers} travelers from ${departureCity}. Your confirmation number is ${referenceNumber}.',
};

/**
 * Ready-made workflows every Process Automation journey starts with, based on
 * the Task Assist Copilot-led design.
 */
export const processAutomationTemplates: ProcessAutomationWorkflow[] = [
  {
    id: 'template-book-iceland-trip',
    intentName: 'BookIcelandTrip',
    name: 'Book an Iceland Trip',
    description:
      'Copilot collects the travel details in the chat and books the package.',
    invocation: ProcessAutomationInvocation.MANUAL,
    formIntro:
      'This form will be visible to the client through the link. Any edits made will show.',
    reviewIntro: 'Information is complete, please review before submitting.',
    conversation: [
      {
        id: 'turn-dates',
        copilotMessage: 'Which dates would you like to travel?',
        customerReply: 'June 15th to June 22nd',
        fills: { departureDate: '2027-06-15', returnDate: '2027-06-22' },
      },
      {
        id: 'turn-travelers',
        copilotMessage:
          'How many travelers, and where are you flying from?',
        customerReply: '2 adults, from Boston',
        fills: { travelers: '2', departureCity: 'Boston' },
      },
      {
        id: 'turn-package',
        copilotMessage:
          'The Golden Circle & Northern Lights package is available for those dates at $2,450 per person. Shall I reserve it?',
        customerReply:
          'Does it include a rental car? We would like to drive the Ring Road.',
        fills: { tripPackage: 'Golden Circle & Northern Lights' },
        needsAttention: true,
      },
      {
        id: 'turn-confirm',
        copilotMessage:
          'To confirm: Golden Circle & Northern Lights package, June 15 - 22, 2 adults from Boston?',
        customerReply: 'Yes, please book it!',
        fills: {},
      },
    ],
    steps: [
      {
        id: 'iceland-trip-form',
        card: icelandTripFormCard,
      },
    ],
    successOutput: icelandTripOutput,
  },
];

/**
 * Returns a deep copy of a template with a unique id so it can be edited
 * independently in a journey.
 * @param templateId - string
 * @example cloneProcessAutomationTemplate('template-book-iceland-trip')
 * @returns ProcessAutomationWorkflow | undefined
 */
export const cloneProcessAutomationTemplate = (templateId: string) => {
  const template = processAutomationTemplates.find(
    (item) => item.id === templateId
  );
  if (!template) return undefined;
  const copy: ProcessAutomationWorkflow = JSON.parse(JSON.stringify(template));
  copy.id = `workflow-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  return copy;
};
